import { useEffect, useContext, useRef } from 'react'
import { useSelector } from 'react-redux'
import { WeekOfTheYearKey } from 'classes/WeekOfTheYearKey'
import { ModalContext, ModalKey } from 'components/Modals/ModalContext';
import { ProfileContext } from 'components/Profile/ProfileContext'

import KinesisProgramWeekKeyIcon from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekKeyIcon'
import KinesisProgramWeekCompletionStatusIcon from 'components/Athlete/v2/KinesisProgramWeeks/KinesisProgramWeekCompletionStatusIcon'

export default function AthleteCalendarProgramWeeks({
    calendarRef,
    calendarEvents
}) {
    const { ShowModal } = useContext(ModalContext)
    const { ReloadTrainingChartData } = useContext(ProfileContext)
    const containerRef = useRef(null)

    const profileType = useSelector((state) => state.athleteProfile.profileType)
    const trainingChartSelectedWeekOfTheYearKey = useSelector(state => state.athleteProfile.trainingChartSelectedWeekOfTheYearKey)
    const trainingChartSegment = useSelector(state => state.athleteProfile.trainingChartSegment)

    const SyncWithCalendarRows = () => {
        if (!calendarRef.current || !containerRef.current) return;

        const calendarEl = calendarRef.current.getApi()?.el;
        if (!calendarEl) return;

        // Find the scrollable container (fc-scroller)
        const scroller = calendarEl.querySelector('.fc-scroller');
        if (!scroller) return;

        // Find the tbody with week rows
        const tbody = calendarEl.querySelector('tbody[role="presentation"]');
        if (!tbody) return;

        // Get all week row elements
        const weekRows = tbody.querySelectorAll('tr[role="row"]');
        if (weekRows.length === 0) return;

        // Get the custom component rows
        const customRows = containerRef.current.querySelectorAll('.program-week-row');
        if (customRows.length !== weekRows.length) return;

        // Get positions for relative calculations
        const containerRect = containerRef.current.getBoundingClientRect();
        const scrollerRect = scroller.getBoundingClientRect();

        // Sync heights and positions
        weekRows.forEach((calendarRow, index) => {
            const customRow = customRows[index];
            if (!customRow) return;

            const rowRect = calendarRow.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            
            // Set height to match calendar row
            customRow.style.height = `${rowRect.height}px`;
            
            // Calculate position: row's viewport top minus container's viewport top

            // Minor hardcodes to get borders lined up
            var positionHardcode = 1.5;
            if (index === 0) positionHardcode = 1;

            const topPosition = rowRect.top - containerRect.top + positionHardcode; 
            
            // Position to align with calendar row
            customRow.style.top = `${topPosition - 2}px`; // Minor hardcodes to get borders lined up
        });
    }

    const GetProgramWeekKey = (weekOfTheYearKey) => {

        const trainingChartWeekData = trainingChartSegment.find(trainingChartData => new WeekOfTheYearKey(trainingChartData.weekOfTheYearKey).equals(new WeekOfTheYearKey(weekOfTheYearKey)))

        if (!trainingChartWeekData) return 'none'

        var programWeekKey = ''
        
        switch (trainingChartWeekData.trainingChartDataKey) {

            case 'empty': 
                programWeekKey = 'none';
                break;

            case 'past': 
                programWeekKey = trainingChartWeekData.overviewFile?.programWeekKey;
                break;
            
            case 'active': 
                programWeekKey = trainingChartWeekData.overviewFile?.programWeekKey;
                break;

            case 'future':
                programWeekKey = trainingChartWeekData.programTableColumn?.volumeData?.programWeekKey;
                break;

            default: 
                programWeekKey = 'none';
                break;
        }

        return programWeekKey
    }

    const GetWeekCompletionStatusIconKey = (weekOfTheYearKey) => {

        const trainingChartWeekData = trainingChartSegment.find(trainingChartData => new WeekOfTheYearKey(trainingChartData.weekOfTheYearKey).equals(new WeekOfTheYearKey(weekOfTheYearKey)))

        if (!trainingChartWeekData) return 'none'

        if (trainingChartWeekData.trainingChartDataKey === 'past') {
            return trainingChartWeekData.trainingChartDataKey + '-' + trainingChartWeekData.overviewFile.weekCompletionStatusKey
        }

        if (
            trainingChartWeekData.trainingChartDataKey === 'active' 
            && trainingChartWeekData.overviewFile.weekCompletionStatusKey === 'skipped'
        ){
            return trainingChartWeekData.trainingChartDataKey + '-' + trainingChartWeekData.overviewFile.weekCompletionStatusKey
        }

        return trainingChartWeekData.trainingChartDataKey
    }

    const ShowProgramWeekInfoModal = (weekOfTheYearKey) => {

        const programWeekKey = GetProgramWeekKey(weekOfTheYearKey)

        if (programWeekKey === 'none') {
            return
        }

        const modalData = {
            key: ModalKey.ProgramWeekInfo,
            size: 'quarter',
            weekOfTheYearKey: weekOfTheYearKey.toString(),
            programWeekKey: programWeekKey,
            RefreshCalendarData: () => ReloadTrainingChartData()
        }

        ShowModal(modalData)
    }

    useEffect(() => {
        SyncWithCalendarRows();
        
        if (!calendarRef.current) return;
        
        const calendarEl = calendarRef.current.getApi()?.el;
        if (!calendarEl) return;

        const scroller = calendarEl.querySelector('.fc-scroller');
        if (!scroller) return;

        // Sync on scroll
        const handleScroll = () => {
            SyncWithCalendarRows();
        };

        // Use ResizeObserver to sync when calendar resizes
        const resizeObserver = new ResizeObserver(() => {
            // Small delay to ensure calendar has finished rendering
            setTimeout(SyncWithCalendarRows, 10);
        });

        const tbody = calendarEl.querySelector('tbody[role="presentation"]');
        if (tbody) {
            resizeObserver.observe(tbody);
        }

        // Also observe the calendar element itself
        resizeObserver.observe(calendarEl);
        resizeObserver.observe(scroller);

        // Listen to scroll events
        scroller.addEventListener('scroll', handleScroll, { passive: true });

        return () => {
            resizeObserver.disconnect();
            scroller.removeEventListener('scroll', handleScroll);
        };
    }, [calendarRef, calendarEvents, trainingChartSelectedWeekOfTheYearKey])

    return (
        <div 
            ref={containerRef}
            className='absolute right-0 top-0 w-8 pointer-events-none overflow-hidden divide-y divide-gray-200'
            style={{ height: '100%' }}
        >
            {[0, 1, 2, 3].map((index) => (
                <div 
                    key={index}
                    className={`
                        program-week-row
                        absolute left-0 right-0
                        flex flex-col items-center justify-center space-y-2
                        pointer-events-auto cursor-pointer
                        ${index === 0 ? 'border-t border-gray-200' : ''}
                    `}
                    onClick={() => {
                        ShowProgramWeekInfoModal(new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(index))
                    }}
                >
                    <KinesisProgramWeekKeyIcon 
                        programWeekKey={GetProgramWeekKey(new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(index))} 
                    />

                    {(profileType === 'group-individual' || profileType === 'individual') && (
                        <div className='scale-90'>
                            <KinesisProgramWeekCompletionStatusIcon 
                                weekCompletionStatusIconKey={GetWeekCompletionStatusIconKey(new WeekOfTheYearKey(trainingChartSelectedWeekOfTheYearKey).add(index))} 
                            />
                        </div>
                    )}
                </div>
            ))}
        </div>
    )
}